/* global globalThis */
// Волна 32 (оборона, гейт isPreW32DefensePlayer у вызывающего кода): риск
// захвата своих городов по правилу «захват в конце раунда, если на кварталах
// нет своих отрядов» и по реальной досягаемости отрядов живых противников с
// учетом того, кто ходит в фазе войны после бота. Волна 37 (T, B1; гейты — у
// вызывающего кода): плюс «фантомный найм» еще не ходивших противников
// (getCityCaptureThreats с withRecruits)
import {calculateAvailableMovementCells} from "../../gameState/calculateAvailableMovementCells";
import {getUnitMaxSizeForFaction, getUnitPrice, getUnitTypeConfig, UNIT_TYPES} from "../../../configs/units";
import {ADDITIONAL_SUPPLY_SPEED} from "../../../configs/supply";
import {RECRUITMENT_SIZE_WITH_COINS_DEFAULT, RECRUITMENT_SIZE_WITH_COINS_WITH_ARSENAL} from "../../../configs/recruitment";
import {
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D10,
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D12,
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D20,
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D8,
} from "../../../configs/policy/totalitarianism";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {PHASES} from "../../../constants/phases";
import {POLICIES} from "../../../constants/policies";
import {COMMON_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {calcCityWinPoints} from "../../gameState/winPoints/calcCityWinPoints";
import {checkIfHexBlockedByEnemyUnit} from "../../gameState/checkIfHexBlockedByEnemyUnit";
import {checkIfCityHasWorkingArsenal} from "../../gameState/recruitment/checkIfCityHasWorkingArsenal";
import {getSameTypeUnitLimit} from "../../gameState/recruitment/checkSameTypeUnitRecruitmentLimit";
import {getSizeRecruitmentPrice} from "../../recruitment/getSizeRecruitmentPrice";
import {getRecruitableUnitTypeIds} from "../../unit/getRecruitableUnitTypeIds";
import {getHexDistance} from "../../coords/getHexDistance";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {getBotGridRenderer, getNeighborHexKeys} from "../botGrid";
import {BOT_EXPECTED_DICE_PENALTY, BOT_WEIGHTS} from "../botConfig";
import {isHexTokenIsBlockerForMovement} from "../../movement/isHexTokenIsBlockerForMovement";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectFinalRound,
  selectHexes,
  selectNextInitiativePlayerId,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectTurnOrder,
  selectUnits,
} from "../../../redux/reducers/sharedStateReducer/selectors";

export const CITY_THREAT_LEVELS = {NONE: 0, NEXT_ROUND: 1, THIS_ROUND: 2};

/**
 * Противники, чья фаза войны в ТЕКУЩЕМ раунде еще впереди и идет ПОСЛЕ игрока.
 * Фаза войны: все, кто еще не завершил фазу. Снабжение и стройка: порядок
 * войны = turnOrder, повернутый к перехватчику инициативы (как в START_NEXT_PHASE)
 */
export const getLaterWarMoverIds = ({phase, turnOrder, nextInitiativePlayerId, phaseCompletedPlayers, currentPlayerId}) => {
  const order = turnOrder || [];
  if (phase === PHASES.WAR) {
    const completed = new Set(phaseCompletedPlayers || []);
    return new Set(order.filter((playerId) => playerId !== currentPlayerId && !completed.has(playerId)));
  }
  let warOrder = order;
  const initiativeIndex = nextInitiativePlayerId ? order.indexOf(nextInitiativePlayerId) : -1;
  if (initiativeIndex > 0) {
    warOrder = [...order.slice(initiativeIndex), ...order.slice(0, initiativeIndex)];
  }
  const ownIndex = warOrder.indexOf(currentPlayerId);
  return new Set(ownIndex < 0 ? [] : warOrder.slice(ownIndex + 1));
};

const hasAnyKey = (cellsSet, candidateKeys) => {
  for (const cellKey of candidateKeys) {
    if (cellsSet.has(cellKey)) {
      return true;
    }
  }
  return false;
};

/**
 * Чистое ядро.
 * getUnitReach(unit) -> {moveCells, horizonCells, baseCells} (Set):
 *  moveCells — куда отряд встанет ближайшим перемещением (с доп. снабжением, если оно у него есть);
 *  horizonCells — плюс клетки только за +1 доп. снабжения (горизонт следующего раунда);
 *  baseCells — базовая дальность: доп. снабжение не потрачено и остается на маневр после боя.
 * Досягаемость считается БЕЗ своих отрядов: держатель не маскирует угрозу от самого себя.
 * getHexToken(hexKey) — токен клетки (маневр возможен только с непустой клетки).
 * THIS_ROUND: враг уже на квартале, либо противник, ходящий после бота, дотягивается до квартала.
 * NEXT_ROUND: дотягивается противник, ходящий раньше, либо только с +1 снабжения.
 * В финальном раунде NEXT_ROUND не ставится: следующего раунда нет, угроза не сбудется.
 * Для оценки «остаться/встать» без пересчета на кандидата в записи города есть:
 *  laterReachedQuarters — кварталы (и занятые своими), куда встают ходящие после бота;
 *  canBreakByManeuver — ходящий после бота снимет держателя и маневром встанет на квартал;
 *  hasEnemyOnQuarter — враг уже стоит на квартале.
 * Волна 35 (G, R): reachesWithinTwoMoves(unit, quarters) -> boolean — отряд встает
 * на один из кварталов за два перемещения. Передан — в записи города есть isSoon:
 * уровень NONE, но живой противник встает на квартал за ≤2 хода. Ходящий после
 * бота может перехватить инициативу и в следующем раунде пойти раньше бота
 * («двойной ход»: R7 после бота, R8 до него) — тогда у бота нет хода на ответ.
 * Уровни level при этом не меняются: их сравнения (NONE/NEXT_ROUND/THIS_ROUND)
 * читают все прежние потребители, а legacy-вызовы isSoon не получают вовсе.
 * Волна 37 (T, B1): phantomRecruits — фантомный найм ходящих после бота
 * (collectPhantomRecruits). Передан (массив, в т.ч. пустой) — в записи города есть:
 *  isThisRoundByRecruit — фантом встает на квартал города в этом раунде (кварталы,
 *    занятые своими, считаются свободными, как laterReachedQuarters);
 *  recruits — такие фантомы; recruitDamage — наибольший ожидаемый урон из них
 *    (держатель должен его пережить);
 *  recruitReachedQuarters — кварталы, куда они встают;
 *  isRecruitHolderAtRisk — держатели есть, фантом встает на СВОБОДНЫЙ квартал, а
 *    держатели не переживают урон отрядов (threatDamage) плюс фантома, смежного им.
 * level от фантомов не меняется (как от isSoon)
 */
export const assessCityCaptureThreats = ({
  ownCities, units, currentPlayerId, laterMoverIds, getUnitReach, getNeighbors, getHexToken, isFinalRound = false,
  reachesWithinTwoMoves = null, phantomRecruits = null,
}) => {
  const ownUnits = [];
  const liveEnemyUnits = [];
  (units || []).forEach((unit) => {
    if (unit.ownerId === currentPlayerId) {
      ownUnits.push(unit);
    } else if (unit.ownerId !== NEUTRAL_PLAYER_ID) {
      liveEnemyUnits.push(unit);
    }
  });
  const ownUnitCells = new Set(ownUnits.map((unit) => unit.hexKey));

  const cityThreats = [];
  const threatByQuarter = new Map();
  (ownCities || []).forEach((city) => {
    const quarters = getAllQuartersOfCity(city);
    const quarterSet = new Set(quarters);
    const freeQuarters = quarters.filter((quarterHexKey) => !ownUnitCells.has(quarterHexKey));
    const holders = ownUnits
      .filter((unit) => quarterSet.has(unit.hexKey))
      .map((unit) => ({id: unit.id, size: unit.size, hexKey: unit.hexKey}));
    const holdersTotalSize = holders.reduce((sum, holder) => sum + holder.size, 0);
    // Клетки, с которых бьют держателей; непустые из них вне кварталов — с них
    // после убийства держателя возможен маневр на его квартал
    const holderAdjacentCells = new Set();
    const holderManeuverCells = new Set();
    holders.forEach((holder) => getNeighbors(holder.hexKey).forEach((adjKey) => {
      holderAdjacentCells.add(adjKey);
      if (!quarterSet.has(adjKey) && getHexToken(adjKey)) {
        holderManeuverCells.add(adjKey);
      }
    }));

    let level = CITY_THREAT_LEVELS.NONE;
    let threatDamage = 0;
    // Противник может снять держателей и в том же раунде оказаться на квартале
    let canBreakHolders = false;
    // Контрфактические признаки для calcCityHolderBonus (см. описание выше)
    const laterReachedQuarters = new Set();
    let canBreakByManeuver = false;
    let hasEnemyOnQuarter = false;
    liveEnemyUnits.forEach((enemy) => {
      const isLaterMover = laterMoverIds.has(enemy.ownerId);
      const expectedDamage = Math.max(0, enemy.size - BOT_EXPECTED_DICE_PENALTY);
      if (quarterSet.has(enemy.hexKey)) {
        level = CITY_THREAT_LEVELS.THIS_ROUND;
        canBreakHolders = true;
        hasEnemyOnQuarter = true;
        // Бой идет только в фазе текущего игрока: бьет держателя, лишь если ходит после бота
        if (!isLaterMover || holders.length === 0) {
          return;
        }
        if (holderAdjacentCells.has(enemy.hexKey)) {
          threatDamage += expectedDamage;
          return;
        }
        // Не смежен держателю, но перейдет с квартала на клетку рядом с ним
        // (например, на свободный центр) и ударит в этой же фазе
        const enemyOnQuarterReach = getUnitReach(enemy);
        if (enemyOnQuarterReach && hasAnyKey(enemyOnQuarterReach.moveCells, holderAdjacentCells)) {
          threatDamage += expectedDamage;
        }
        return;
      }
      // Отсечение до BFS: путь не короче гекс-дистанции (скорость + доп. снабжение + смежность)
      const maxRange = (getUnitTypeConfig(enemy.type)?.speed || 2) + ADDITIONAL_SUPPLY_SPEED + 1;
      if (!quarters.some((quarterHexKey) => getHexDistance(enemy.hexKey, quarterHexKey) <= maxRange)) {
        return;
      }
      const reach = getUnitReach(enemy);
      if (!reach) {
        return;
      }
      const reachesQuarter = hasAnyKey(reach.moveCells, quarters);
      if (isLaterMover) {
        if (reachesQuarter) {
          level = CITY_THREAT_LEVELS.THIS_ROUND;
          if (hasAnyKey(reach.moveCells, freeQuarters)) {
            canBreakHolders = true;
          }
          quarters.forEach((quarterHexKey) => {
            if (reach.moveCells.has(quarterHexKey)) {
              laterReachedQuarters.add(quarterHexKey);
            }
          });
        }
        if (holders.length > 0
          && (holderAdjacentCells.has(enemy.hexKey) || hasAnyKey(reach.moveCells, holderAdjacentCells))
        ) {
          threatDamage += expectedDamage;
          // Маневр (этап 5): бился, доп. снабжение не потрачено на ход, стоит на непустой клетке
          if (enemy.additionalSupply === true
            && (holderManeuverCells.has(enemy.hexKey) || hasAnyKey(reach.baseCells, holderManeuverCells))
          ) {
            canBreakHolders = true;
            canBreakByManeuver = true;
          }
        }
      }
      if (!isFinalRound && level === CITY_THREAT_LEVELS.NONE
        && (reachesQuarter || hasAnyKey(reach.horizonCells, quarters))) {
        level = CITY_THREAT_LEVELS.NEXT_ROUND;
      }
    });

    const entry = {
      city,
      quarters,
      level,
      cityWinPoints: calcCityWinPoints(city),
      holders,
      holdersTotalSize,
      threatDamage,
      canBreakHolders,
      laterReachedQuarters,
      canBreakByManeuver,
      hasEnemyOnQuarter,
      isHolderAtRisk: level === CITY_THREAT_LEVELS.THIS_ROUND && holders.length > 0
        && canBreakHolders && holdersTotalSize <= threatDamage,
      // Свой отряд на казарме города (в т.ч. нанятый этой фазой) встает на смежный квартал перемещением
      hasOwnUnitOnBarracks: (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).some((cellKey) => ownUnitCells.has(cellKey)),
    };
    if (reachesWithinTwoMoves) {
      // В финальном раунде захват через два хода не сбудется — «скоро» не ставится
      entry.isSoon = level === CITY_THREAT_LEVELS.NONE && !isFinalRound && liveEnemyUnits.some((enemy) => {
        // Отсечение до BFS: за ход не дальше скорости + доп. снабжение
        const maxRange = 2 * ((getUnitTypeConfig(enemy.type)?.speed || 2) + ADDITIONAL_SUPPLY_SPEED) + 1;
        return quarters.some((quarterHexKey) => getHexDistance(enemy.hexKey, quarterHexKey) <= maxRange)
          && reachesWithinTwoMoves(enemy, quarters);
      });
    }
    if (phantomRecruits) {
      const recruits = phantomRecruits.filter((recruit) => hasAnyKey(recruit.stopCells, quarters));
      let recruitDamage = 0;
      let holderAdjacentRecruitDamage = 0;
      let canRecruitTakeFreeQuarter = false;
      const recruitReachedQuarters = new Set();
      recruits.forEach((recruit) => {
        recruitDamage = Math.max(recruitDamage, recruit.expectedDamage);
        quarters.forEach((quarterHexKey) => {
          if (recruit.stopCells.has(quarterHexKey)) {
            recruitReachedQuarters.add(quarterHexKey);
          }
        });
        if (hasAnyKey(recruit.stopCells, freeQuarters)) {
          canRecruitTakeFreeQuarter = true;
        }
        // Бой — после перемещения: бьет держателя фантом, вставший рядом с ним.
        // Маневра у нового отряда нет (доп. снабжения нет) — после боя он не
        // перейдет на квартал убитого: город возьмет, только встав на свободный
        if (holders.length > 0 && hasAnyKey(recruit.stopCells, holderAdjacentCells)) {
          holderAdjacentRecruitDamage = Math.max(holderAdjacentRecruitDamage, recruit.expectedDamage);
        }
      });
      entry.isThisRoundByRecruit = recruits.length > 0;
      entry.recruits = recruits;
      entry.recruitDamage = recruitDamage;
      entry.recruitReachedQuarters = recruitReachedQuarters;
      entry.isRecruitHolderAtRisk = holders.length > 0 && canRecruitTakeFreeQuarter
        && holdersTotalSize <= threatDamage + holderAdjacentRecruitDamage;
    }
    cityThreats.push(entry);
    quarters.forEach((quarterHexKey) => threatByQuarter.set(quarterHexKey, entry));
  });
  return {cityThreats, threatByQuarter};
};

/**
 * Волна 35: клетки, где отряд может закончить одно перемещение, — та же семантика,
 * что у calculateAvailableMovementCells (чужой отряд, гора и море не проходятся;
 * свой отряд проходится по непустой клетке, а по пустой — только при ЭКОАНАРХИЗМЕ;
 * вход на пустую клетку без ЭКОАНАРХИЗМА кончает ход), но без путей и с поиском
 * отряда на клетке по Map: второй ход горизонта «скоро» считается с десятков
 * клеток на отряд. unitByHex — отряд на клетке (без своих отрядов бота)
 */
export const calcMoveStopCellsFast = ({
  gridRenderer, hexes, unitByHex, ownerId, startHexKey, speed, isEcoanarchism,
}) => {
  const stopCells = new Set();
  const visited = new Set([startHexKey]);
  let frontier = [startHexKey];
  for (let depth = 1; depth <= speed && frontier.length > 0; depth += 1) {
    const nextFrontier = [];
    for (const hexKey of frontier) {
      for (const neighborKey of getNeighborHexKeys(hexKey, gridRenderer)) {
        if (visited.has(neighborKey)) {
          continue;
        }
        visited.add(neighborKey);
        const unitOnCell = unitByHex.get(neighborKey);
        const hexToken = hexes[neighborKey];
        if ((unitOnCell && unitOnCell.ownerId !== ownerId) || isHexTokenIsBlockerForMovement(hexToken)) {
          continue;
        }
        if (!unitOnCell) {
          stopCells.add(neighborKey);
        }
        if (hexToken || isEcoanarchism) {
          nextFrontier.push(neighborKey);
        }
      }
    }
    frontier = nextFrontier;
  }
  return stopCells;
};

// ТОТАЛИТАРИЗМ: стартовый размер нового отряда (редьюсер RECRUIT_UNIT), прочие — 1
const TOTALITARIANISM_RECRUIT_SIZES = {
  [UNIT_TYPES.D10.id]: POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D10,
  [UNIT_TYPES.D20.id]: POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D20,
  [UNIT_TYPES.D8.id]: POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D8,
  [UNIT_TYPES.D12.id]: POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D12,
};

/**
 * Волна 37 (T, B1): «фантомный найм» — противник, который ходит в фазе войны
 * ПОСЛЕ бота, нанимает отряд на своей казарме и в той же фазе ведет его на
 * квартал города бота: этап найма идет до перемещения, запрета ходить нанятым
 * нет. Модели W32 такой отряд не виден — его еще нет на поле. Откуда: на 200
 * прод-реплеях 4.2% захватов городов золото+ — только фантомом (все отрядом,
 * нанятым ходящим после бота: кавалерией 32 из 60, пехотой с казармы в 2
 * клетках — 28); фантом живого противника сбывается в 36%, бота — в 4.5%.
 *
 * На каждого ходящего после бота (не нейтрала) — один тип: самый быстрый из
 * доступных ему по фракции (getRecruitableUnitTypeIds: гномам нельзя кавалерию,
 * великанам — только d20), монетам (getUnitPrice со скидками политик) и лимиту
 * типа правил (getSameTypeUnitLimit), при равной скорости — дешевле. Запись — на
 * каждую его казарму: свободную (на ней нет отряда), рабочую (ЗК и повреждения —
 * checkIfHexBlockedByEnemyUnit; отряды бота блокируют казарму противника) и не
 * дальше скорости типа от кварталов бота (дешевая отсечка до BFS).
 * Размер — стартовый (1, с ТОТАЛИТАРИЗМОМ больше) плюс докупка на остаток монет
 * с незаблокированных кварталов его города (×2 с рабочим арсеналом), не выше
 * максимума. Досягаемость — одно перемещение нового отряда без доп. снабжения
 * (calcMoveStopCellsFast по unitByHex — отряды поля без отрядов бота: держатель
 * не маскирует угрозу, как в W32). В последнем раунде партии найма нет — [].
 * Оценка верхняя: монет хватает на найм на каждой казарме сразу.
 *
 * Возвращает [{enemyId, isEnemyBot, barracksHexKey, unitTypeId, speed, price,
 *   expectedSize, expectedDamage, stopCells: Set}] в порядке игроков и их казарм
 */
export const collectPhantomRecruits = ({
  players, units, hexes, citiesCache, specialBuildings, buildingsDamage, gridRenderer,
  currentPlayerId, laterMoverIds, ownQuarterKeys, isFinalRound = false,
}) => {
  if (isFinalRound || !ownQuarterKeys || ownQuarterKeys.length === 0 || !laterMoverIds || laterMoverIds.size === 0) {
    return [];
  }
  const occupiedCells = new Set();
  const unitByHex = new Map();
  const unitCountByOwnerType = new Map();
  (units || []).forEach((unit) => {
    occupiedCells.add(unit.hexKey);
    if (unit.ownerId !== currentPlayerId && !unitByHex.has(unit.hexKey)) {
      unitByHex.set(unit.hexKey, unit);
    }
    const ownerTypeKey = `${unit.ownerId}|${unit.type}`;
    unitCountByOwnerType.set(ownerTypeKey, (unitCountByOwnerType.get(ownerTypeKey) || 0) + 1);
  });
  const recruits = [];
  (players || []).forEach((enemy) => {
    if (!laterMoverIds.has(enemy.id) || enemy.id === NEUTRAL_PLAYER_ID || enemy.color === NEUTRAL_PLAYER_COLOR) {
      return;
    }
    const enemyCities = (citiesCache && citiesCache[enemy.color]) || [];
    if (enemyCities.length === 0) {
      return;
    }
    const coins = enemy.coins || 0;
    let best = null;
    getRecruitableUnitTypeIds(enemy).forEach((unitTypeId) => {
      const {price} = getUnitPrice(unitTypeId, enemy);
      if (price > coins
        || (unitCountByOwnerType.get(`${enemy.id}|${unitTypeId}`) || 0) >= getSameTypeUnitLimit(enemy, unitTypeId)
      ) {
        return;
      }
      const config = getUnitTypeConfig(unitTypeId);
      if (!best || config.speed > best.config.speed || (config.speed === best.config.speed && price < best.price)) {
        best = {unitTypeId, price, config};
      }
    });
    if (!best) {
      return;
    }
    const {unitTypeId, price, config} = best;
    const speed = config.speed;
    const isEcoanarchism = isPlayerPolicy(enemy, POLICIES.ECOANARCHISM);
    const baseSize = isPlayerPolicy(enemy, POLICIES.TOTALITARIANISM) ? (TOTALITARIANISM_RECRUIT_SIZES[unitTypeId] || 1) : 1;
    const maxSize = getUnitMaxSizeForFaction(unitTypeId, enemy.factionId);
    const {sizePrice} = getSizeRecruitmentPrice(config, enemy);
    const seenBarracks = new Set();
    enemyCities.forEach((city) => {
      (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((barracksHexKey) => {
        // Казарма двух городов — одна запись (размер — с кварталов первого, как в игре)
        if (seenBarracks.has(barracksHexKey)) {
          return;
        }
        seenBarracks.add(barracksHexKey);
        if (occupiedCells.has(barracksHexKey)
          || !ownQuarterKeys.some((quarterHexKey) => getHexDistance(barracksHexKey, quarterHexKey) <= speed)
          || checkIfHexBlockedByEnemyUnit(
            barracksHexKey, TOKENS.BARRACKS, enemy.id, units, hexes, specialBuildings, buildingsDamage
          )
        ) {
          return;
        }
        // Докупка размера — по разу с каждого незаблокированного квартала города
        const freeSizeQuarters = getAllQuartersOfCity(city).filter((quarterHexKey) => !checkIfHexBlockedByEnemyUnit(
          quarterHexKey, TOKENS.QUARTER, enemy.id, units, hexes, specialBuildings, buildingsDamage
        )).length;
        const purchases = sizePrice > 0
          ? Math.min(freeSizeQuarters, Math.floor((coins - price) / sizePrice))
          : freeSizeQuarters;
        const perPurchase = checkIfCityHasWorkingArsenal(city, enemy.id, units, hexes, specialBuildings, buildingsDamage)
          ? RECRUITMENT_SIZE_WITH_COINS_WITH_ARSENAL
          : RECRUITMENT_SIZE_WITH_COINS_DEFAULT;
        const expectedSize = Math.min(maxSize, baseSize + Math.max(0, purchases) * perPurchase);
        recruits.push({
          enemyId: enemy.id,
          isEnemyBot: Boolean(enemy.isBot),
          barracksHexKey,
          unitTypeId,
          speed,
          price,
          expectedSize,
          expectedDamage: Math.max(0, expectedSize - BOT_EXPECTED_DICE_PENALTY),
          stopCells: calcMoveStopCellsFast({
            gridRenderer, hexes, unitByHex, ownerId: enemy.id, startHexKey: barracksHexKey, speed, isEcoanarchism,
          }),
        });
      });
    });
  });
  return recruits;
};

// Кэш фантомного найма на позицию (как reachMemo, но свой): найм и перемещение
// зовут угрозу на каждом тике. Ключ шире, чем у досягаемости: отряды бота
// занимают и блокируют (ЗК) казармы противника, а сам бот в этой фазе ходит, —
// поэтому все отряды (с владельцем: соблазнение суккубов его меняет); монеты,
// политика и фракция ходящих после бота; раунд и лимит раундов; плюс ссылки на
// клетки поля, кэш городов (казармы, кварталы, арсеналы), строения и повреждения
let phantomMemo = null;
const EMPTY_OBJECT = {};

const getPhantomRecruits = (state, currentPlayer, laterMoverIds, ownCities, gridRenderer) => {
  const players = selectPlayers(state) || [];
  const units = selectUnits(state) || [];
  const hexes = selectHexes(state);
  const citiesCache = selectCitiesCache(state);
  const specialBuildings = selectSpecialBuildings(state) || EMPTY_OBJECT;
  const buildingsDamage = selectBuildingsDamage(state) || EMPTY_OBJECT;
  const key = [
    currentPlayer.id, selectPhase(state), selectRound(state), selectFinalRound(state), [...laterMoverIds].join(','),
    players.filter((player) => laterMoverIds.has(player.id))
      .map((player) => `${player.id}:${player.coins || 0}:${player.policy?.id || ''}:${player.factionId || ''}`)
      .join(','),
    units.map((unit) => `${unit.id}@${unit.hexKey}:${unit.ownerId}:${unit.type}:${unit.isSupplyRecruit ? 1 : 0}`)
      .join('|'),
  ].join('#');
  if (phantomMemo && phantomMemo.key === key && phantomMemo.hexes === hexes && phantomMemo.citiesCache === citiesCache
    && phantomMemo.specialBuildings === specialBuildings && phantomMemo.buildingsDamage === buildingsDamage
    && phantomMemo.gridRenderer === gridRenderer
  ) {
    return phantomMemo.recruits;
  }
  const recruits = collectPhantomRecruits({
    players,
    units,
    hexes,
    citiesCache,
    specialBuildings,
    buildingsDamage,
    gridRenderer,
    currentPlayerId: currentPlayer.id,
    laterMoverIds,
    ownQuarterKeys: ownCities.flatMap((city) => getAllQuartersOfCity(city)),
    isFinalRound: checkIfFinalRound(state),
  });
  phantomMemo = {key, hexes, citiesCache, specialBuildings, buildingsDamage, gridRenderer, recruits};
  return recruits;
};

// Кэш досягаемости: чужие отряды в фазе бота не ходят, BFS на ближнего врага
// считается один раз и переиспользуется тиками перемещения, маневра и найма.
// Ключ: игрок, фаза, раунд, ЭКОАНАРХИЗМ противников (проходимость пустых клеток),
// id/клетка/тип/доп. снабжение чужих отрядов плюс ссылки на hexes и сетку
let reachMemo = null;

/**
 * Волна 35 (G, R): withSoon — записи городов получают isSoon (горизонт двух ходов,
 * см. assessCityCaptureThreats): открытость гарнизонов и найм держателей и
 * заслонов. Досягаемость за два хода кэшируется в том же reachMemo (на фазу),
 * второй ход считается только с клеток, откуда отряд дотянется до своих
 * кварталов. Без withSoon (перемещение, legacy) — прежний расчет.
 * Волна 37 (T, B1): withRecruits — записи городов получают поля фантомного найма
 * ходящих после бота (isThisRoundByRecruit, recruits, recruitDamage,
 * recruitReachedQuarters, isRecruitHolderAtRisk — см. assessCityCaptureThreats и
 * collectPhantomRecruits); humanRecruitsOnly — фантомы только живых противников
 * (без isBot). Фантомы кэшируются отдельно от reachMemo (phantomMemo) и общие у
 * записей и вызовов на одной позиции — не мутировать. level от них не меняется.
 * Без withRecruits — прежний расчет и прежний кэш дословно
 */
export const getCityCaptureThreats = (state, currentPlayer, {
  withSoon = false, withRecruits = false, humanRecruitsOnly = false,
} = {}) => {
  const units = selectUnits(state) || [];
  const hexes = selectHexes(state);
  const players = selectPlayers(state) || [];
  const gridRenderer = getBotGridRenderer(state);
  const phase = selectPhase(state);
  const turnOrder = selectTurnOrder(state) || [];
  const laterMoverIds = getLaterWarMoverIds({
    phase,
    turnOrder: turnOrder.length > 0 ? turnOrder : players.map((player) => player.id),
    nextInitiativePlayerId: selectNextInitiativePlayerId(state),
    phaseCompletedPlayers: selectPhaseCompletedPlayers(state),
    currentPlayerId: currentPlayer.id,
  });
  const unitsForEnemyReach = units.filter((unit) => unit.ownerId !== currentPlayer.id);
  const ecoanarchistIds = players
    .filter((player) => isPlayerPolicy(player, POLICIES.ECOANARCHISM))
    .map((player) => player.id)
    .join(',');
  const fingerprint = `${currentPlayer.id}#${phase}#${selectRound(state)}#${ecoanarchistIds}#${unitsForEnemyReach
    .map((unit) => `${unit.id}@${unit.hexKey}:${unit.type}:${unit.additionalSupply ? 1 : 0}`).join('|')}`;
  if (!reachMemo || reachMemo.fingerprint !== fingerprint
    || reachMemo.hexes !== hexes || reachMemo.gridRenderer !== gridRenderer) {
    reachMemo = {fingerprint, hexes, gridRenderer, reachByUnitId: new Map()};
  }
  const memo = reachMemo;
  const getUnitReach = (unit) => {
    let reach = memo.reachByUnitId.get(unit.id);
    if (!reach) {
      const {cells, additionalSupplyCells} = calculateAvailableMovementCells(
        gridRenderer, hexes, unitsForEnemyReach, {...unit, additionalSupply: true}, players
      );
      const supplyOnlyCells = new Set(additionalSupplyCells);
      const horizonCells = new Set(cells);
      const baseCells = new Set(cells.filter((cellKey) => !supplyOnlyCells.has(cellKey)));
      reach = {baseCells, horizonCells, moveCells: unit.additionalSupply === true ? horizonCells : baseCells};
      memo.reachByUnitId.set(unit.id, reach);
    }
    return reach;
  };
  const ownCities = selectCitiesCache(state)?.[currentPlayer.color] || [];
  let reachesWithinTwoMoves = null;
  if (withSoon) {
    // Второй ход — только с клеток первого хода, откуда до кварталов города
    // дотянуться одним перемещением, и до первого попадания. Досягаемость второго
    // хода (по отряду и клетке старта) кэшируется в reachMemo на фазу: она зависит
    // только от чужих отрядов и клеток поля, как и первый ход
    if (!memo.secondMoveByKey) {
      memo.secondMoveByKey = new Map();
      memo.unitByHex = new Map();
      unitsForEnemyReach.forEach((unit) => {
        if (!memo.unitByHex.has(unit.hexKey)) {
          memo.unitByHex.set(unit.hexKey, unit);
        }
      });
    }
    const getSecondMoveCells = (unit, startHexKey) => {
      const key = `${unit.id}@${startHexKey}`;
      let cells = memo.secondMoveByKey.get(key);
      if (!cells) {
        const owner = players.find((player) => player.id === unit.ownerId);
        cells = calcMoveStopCellsFast({
          gridRenderer,
          hexes,
          unitByHex: memo.unitByHex,
          ownerId: unit.ownerId,
          startHexKey,
          speed: (getUnitTypeConfig(unit.type)?.speed || 2) + ADDITIONAL_SUPPLY_SPEED,
          isEcoanarchism: isPlayerPolicy(owner, POLICIES.ECOANARCHISM),
        });
        memo.secondMoveByKey.set(key, cells);
      }
      return cells;
    };
    reachesWithinTwoMoves = (unit, quarters) => {
      const firstMove = getUnitReach(unit);
      if (hasAnyKey(firstMove.horizonCells, quarters)) {
        return true;
      }
      const stepRange = (getUnitTypeConfig(unit.type)?.speed || 2) + ADDITIONAL_SUPPLY_SPEED + 1;
      for (const cellKey of firstMove.horizonCells) {
        if (quarters.some((quarterHexKey) => getHexDistance(cellKey, quarterHexKey) <= stepRange)
          && hasAnyKey(getSecondMoveCells(unit, cellKey), quarters)
        ) {
          return true;
        }
      }
      return false;
    };
  }
  let phantomRecruits = null;
  if (withRecruits) {
    const recruits = getPhantomRecruits(state, currentPlayer, laterMoverIds, ownCities, gridRenderer);
    phantomRecruits = humanRecruitsOnly ? recruits.filter((recruit) => !recruit.isEnemyBot) : recruits;
  }
  return assessCityCaptureThreats({
    ownCities,
    units,
    currentPlayerId: currentPlayer.id,
    laterMoverIds,
    getUnitReach,
    getNeighbors: (hexKey) => getNeighborHexKeys(hexKey, gridRenderer),
    getHexToken: (hexKey) => hexes[hexKey],
    // В раунде 13 риск следующего раунда фантомный в любой фазе (найм в нем тоже
    // запрещен); раунд уже входит в ключ кэша досягаемости
    isFinalRound: checkIfFinalRound(state),
    reachesWithinTwoMoves,
    phantomRecruits,
  });
};

// Калибровка без правки кода (глобал из симуляций); в проде оверрайд не задан
const getNextRoundHolderFactor = () => (
  typeof globalThis.__w32HolderNextRoundFactor === 'number'
    ? globalThis.__w32HolderNextRoundFactor
    : BOT_WEIGHTS.W32_CITY_HOLDER_NEXT_ROUND_FACTOR
);

/**
 * Ценность единственного держателя города с cityWinPoints очками — в валюте захвата
 * (CAPTURE_ENEMY_QUARTER = 100): основа бонуса держателя волны 32 и, в режиме
 * 'soft' гарнизонов волны 35 (G), удержания квартала гарнизоном
 */
export const calcCityHolderValue = (cityWinPoints) => Math.min(
  BOT_WEIGHTS.W32_CITY_HOLDER_MAX,
  BOT_WEIGHTS.W32_CITY_HOLDER_BASE + BOT_WEIGHTS.W32_CITY_HOLDER_PER_WIN_POINT * cityWinPoints
);

/**
 * Бонус позиции на квартале cellKey города cityThreat для отряда unit (0 — бонуса нет).
 * Оценка контрфактическая: квартал оценивающего отряда считается свободным, а
 * «остаться» и «встать» оцениваются одинаково (иначе оба держателя занятого
 * города теряли бонус и по очереди уходили).
 * Волна 35: подчасти D2 (ценность города целиком и пол при NEXT_ROUND) и D4 (бонус
 * при угрозе «скоро») удалены по A/B — расчет прежний для всех ботов
 */
export const calcCityHolderBonus = (cityThreat, unit, cellKey) => {
  if (!cityThreat || cityThreat.level === CITY_THREAT_LEVELS.NONE) {
    return 0;
  }
  const cityValue = calcCityHolderValue(cityThreat.cityWinPoints);
  let otherHoldersCount = 0;
  let otherHoldersSize = 0;
  cityThreat.holders.forEach((holder) => {
    if (holder.id !== unit.id) {
      otherHoldersCount += 1;
      otherHoldersSize += holder.size;
    }
  });
  if (otherHoldersCount === 0) {
    // Первый держатель или сам отряд — единственный держатель: бонус есть и у «остаться»
    return cityThreat.level === CITY_THREAT_LEVELS.THIS_ROUND
      ? cityValue
      : cityValue * getNextRoundHolderFactor();
  }
  if (cityThreat.level !== CITY_THREAT_LEVELS.THIS_ROUND || otherHoldersSize > cityThreat.threatDamage) {
    return 0; // остальные держатели и так переживут урон
  }
  // Ходящий после бота сначала перемещается, потом бьется: на занятый квартал он не
  // встанет. Если этот квартал — единственный свободный, куда он дотягивается, и
  // маневра нет, занять его значит сорвать захват при любом размере отряда
  const laterReachedQuarters = cityThreat.laterReachedQuarters;
  const isReachedHere = Boolean(laterReachedQuarters && laterReachedQuarters.has(cellKey));
  let canBreakWithoutUnit = Boolean(cityThreat.hasEnemyOnQuarter || cityThreat.canBreakByManeuver);
  if (!canBreakWithoutUnit && laterReachedQuarters) {
    for (const quarterHexKey of laterReachedQuarters) {
      if (quarterHexKey !== cellKey && !cityThreat.holders.some(
        (holder) => holder.id !== unit.id && holder.hexKey === quarterHexKey
      )) {
        canBreakWithoutUnit = true;
        break;
      }
    }
  }
  const blocksCapture = isReachedHere && !canBreakWithoutUnit;
  // Иначе захват возможен и без этого квартала — нужен размер, переживающий урон
  const survivesDamage = (cityThreat.canBreakHolders || isReachedHere)
    && otherHoldersSize + unit.size > cityThreat.threatDamage;
  return blocksCapture || survivesDamage ? cityValue * BOT_WEIGHTS.W32_CITY_HOLDER_REINFORCE_FACTOR : 0;
};

/** Единственные держатели кварталов городов с риском >= minLevel: Map<unitId, кварталы города>. */
export const getPinnedHolders = (cityThreats, minLevel) => {
  const pinned = new Map();
  (cityThreats || []).forEach((threat) => {
    if (threat.level >= minLevel && threat.holders.length === 1) {
      pinned.set(threat.holders[0].id, threat.quarters);
    }
  });
  return pinned;
};

/** Казармы городов с риском >= minLevel без живучего держателя: Map<barracksHexKey, level>. */
export const getGuardNeededCityBarracks = (cityThreats, minLevel, {skipCitiesWithOwnUnitOnBarracks = false} = {}) => {
  const barracksLevels = new Map();
  (cityThreats || []).forEach((threat) => {
    if (threat.level < minLevel || (threat.holders.length > 0 && !threat.isHolderAtRisk)) {
      return;
    }
    // Для найма нового отряда: свой отряд уже на казарме города — второй заслон не нужен
    if (skipCitiesWithOwnUnitOnBarracks && threat.hasOwnUnitOnBarracks) {
      return;
    }
    (threat.city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((cellKey) => {
      barracksLevels.set(cellKey, Math.max(threat.level, barracksLevels.get(cellKey) || 0));
    });
  });
  return barracksLevels;
};

/** Враг стоит на своем квартале, и смежные свои отряды добивают его по матожиданию. */
export const hasKillableEnemyOnOwnQuarter = ({units, ownQuarters, currentPlayerId, getNeighbors}) =>
  (units || []).some((enemy) => {
    if (enemy.ownerId === currentPlayerId || enemy.ownerId === NEUTRAL_PLAYER_ID || !ownQuarters.has(enemy.hexKey)) {
      return false;
    }
    const neighbors = new Set(getNeighbors(enemy.hexKey));
    const adjacentOwnDamage = units.reduce((sum, ownUnit) => (
      ownUnit.ownerId === currentPlayerId && neighbors.has(ownUnit.hexKey)
        ? sum + Math.max(0, ownUnit.size - BOT_EXPECTED_DICE_PENALTY)
        : sum
    ), 0);
    return adjacentOwnDamage >= enemy.size;
  });
